export type WorkerType = "exif" | "las";

export class WorkerPool {
  private workers: { worker: Worker; busy: boolean; id: number }[] = [];
  private queue: {
    data: any;
    resolve: (value: any) => void;
    reject: (reason?: any) => void;
    onProgress?: (progress: any) => void;
  }[] = [];
  private nextTaskId = 0;
  private workerType: WorkerType;

  constructor(
    workerType: WorkerType,
    poolSize: number = Math.min(navigator.hardwareConcurrency || 4, 8)
  ) {
    this.workerType = workerType;

    for (let i = 0; i < poolSize; i++) {
      let worker: Worker;

      if (workerType === "exif") {
        worker = new Worker(new URL("./exif.worker.ts", import.meta.url), {
          type: "module",
        });
      } else {
        worker = new Worker(new URL("./las.worker.ts", import.meta.url), {
          type: "module",
        });
      }

      this.workers.push({
        worker,
        busy: false,
        id: i,
      });
    }
  }

  process(data: any, onProgress?: (progress: any) => void): Promise<any> {
    return new Promise((resolve, reject) => {
      const availableWorker = this.workers.find((w) => !w.busy);

      if (availableWorker) {
        this.executeWork(availableWorker, data, resolve, reject, onProgress);
      } else {
        this.queue.push({ data, resolve, reject, onProgress });
      }
    });
  }

  private processNextInQueue(workerObj: {
    worker: Worker;
    busy: boolean;
    id: number;
  }) {
    if (this.queue.length > 0) {
      const next = this.queue.shift()!;
      this.executeWork(
        workerObj,
        next.data,
        next.resolve,
        next.reject,
        next.onProgress
      );
    }
  }

  private executeWork(
    workerObj: { worker: Worker; busy: boolean; id: number },
    data: any,
    resolve: (value: any) => void,
    reject: (reason?: any) => void,
    onProgress?: (progress: any) => void
  ) {
    workerObj.busy = true;
    const taskId = this.nextTaskId++;

    const handleMessage = (e: MessageEvent) => {
      if (this.workerType === "exif") {
        if (e.data?.id === taskId) {
          workerObj.worker.removeEventListener("message", handleMessage);
          workerObj.worker.removeEventListener("error", handleError);
          workerObj.busy = false;

          if (e.data.error) {
            reject(new Error(e.data.error));
          } else {
            resolve(e.data.result);
          }

          this.processNextInQueue(workerObj);
        }
      } else if (this.workerType === "las") {
        if (e.data?.type === "done") {
          workerObj.worker.removeEventListener("message", handleMessage);
          workerObj.worker.removeEventListener("error", handleError);
          workerObj.busy = false;

          resolve(e.data);

          this.processNextInQueue(workerObj);
        } else if (e.data?.type === "error") {
          workerObj.worker.removeEventListener("message", handleMessage);
          workerObj.worker.removeEventListener("error", handleError);
          workerObj.busy = false;

          reject(new Error(e.data.message));

          this.processNextInQueue(workerObj);
        } else if (e.data?.type === "progress" && onProgress) {
          // Forward progress messages to the callback
          onProgress(e.data);
        }
      }
    };

    const handleError = (e: ErrorEvent) => {
      workerObj.worker.removeEventListener("message", handleMessage);
      workerObj.worker.removeEventListener("error", handleError);
      workerObj.busy = false;
      reject(e);

      this.processNextInQueue(workerObj);
    };

    workerObj.worker.addEventListener("message", handleMessage);
    workerObj.worker.addEventListener("error", handleError);

    if (this.workerType === "exif") {
      workerObj.worker.postMessage({ id: taskId, buffer: data });
    } else if (this.workerType === "las") {
      workerObj.worker.postMessage(data);
    }
  }

  terminate() {
    this.workers.forEach((w) => w.worker.terminate());
    this.workers = [];
    this.queue = [];
  }

  getStats() {
    return {
      totalWorkers: this.workers.length,
      busyWorkers: this.workers.filter((w) => w.busy).length,
      queueLength: this.queue.length,
    };
  }
}

export class ExifWorkerPool extends WorkerPool {
  constructor(poolSize?: number) {
    super("exif", poolSize);
  }

  process(
    buffer: ArrayBuffer,
    onProgress?: (progress: any) => void
  ): Promise<any> {
    return super.process(buffer, onProgress);
  }
}

export class LasWorkerPool extends WorkerPool {
  constructor(poolSize?: number) {
    super("las", 1);
  }

  process(data: any, onProgress?: (progress: any) => void): Promise<any> {
    return super.process(data, onProgress);
  }
}
